import { useMemo } from "react";
import {
  Loader2,
  Plus,
  Wallet,
  Clock,
  Pencil,
  Trash2,
  MoreHorizontal,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  SalaryStatusBadge,
  formatHuf,
  formatHours,
  formatPayMonth,
} from "@/components/SalaryFormSheet";
import { Th, Td, openSalaryPayslip } from "./tabHelpers";

export function SalariesTab({
  salaries,
  loading,
  company,
  site,
  payingId,
  onCreate,
  onAccount,
  onPay,
  onDelete,
}) {
  const totals = useMemo(() => {
    return salaries.reduce(
      (acc, s) => {
        acc.gross +=
          Number(s.gross_salary || 0) +
          Number(s.gross_commission || 0) +
          Number(s.gross_bonus || 0);
        acc.net +=
          Number(s.net_salary || 0) +
          Number(s.net_commission || 0) +
          Number(s.net_bonus || 0);
        acc.hours += Number(s.hours_worked || 0);
        return acc;
      },
      { gross: 0, net: 0, hours: 0 }
    );
  }, [salaries]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SalaryStatCard
          icon={<Wallet className="h-3.5 w-3.5" strokeWidth={1.75} />}
          label="Összes bejegyzés"
          value={salaries.length}
        />
        <SalaryStatCard
          icon={<Wallet className="h-3.5 w-3.5" strokeWidth={1.75} />}
          label="Összes bruttó (Ft)"
          value={formatHuf(totals.gross)}
          mono
        />
        <SalaryStatCard
          icon={<Wallet className="h-3.5 w-3.5" strokeWidth={1.75} />}
          label="Összes nettó (Ft)"
          value={formatHuf(totals.net)}
          mono
        />
        <SalaryStatCard
          icon={<Clock className="h-3.5 w-3.5" strokeWidth={1.75} />}
          label="Ledolgozott óra"
          value={formatHours(totals.hours)}
          mono
        />
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          A munkavállaló minden havi bér rögzítését itt találod.
          {company && (
            <>
              {" "}Alapértelmezett munkáltató:{" "}
              <span className="font-medium text-foreground">{company.name}</span>
              {site && (
                <>
                  {" · "}
                  <span className="font-medium text-foreground">{site.name}</span>
                </>
              )}
              .
            </>
          )}
        </p>
        <Button size="sm" data-testid="new-salary-button" onClick={onCreate}>
          <Plus className="h-4 w-4" />
          Új bér rögzítése
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
        {loading ? (
          <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Bérek betöltése…
          </div>
        ) : salaries.length === 0 ? (
          <div
            data-testid="salaries-empty-state"
            className="flex flex-col items-center gap-3 px-6 py-14 text-center"
          >
            <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
              <Wallet className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
            </div>
            <p className="text-sm font-medium">Még nincs rögzített bér</p>
            <p className="text-xs text-muted-foreground">
              Add hozzá a havi bruttó/nettó béreket, jutalékokat és bónuszokat.
            </p>
            <Button variant="outline" size="sm" className="mt-2 h-8" onClick={onCreate}>
              <Plus className="h-3.5 w-3.5" /> Új bér rögzítése
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="salaries-table">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                  <Th>Tárgyhó</Th>
                  <Th className="text-right">Bruttó bér</Th>
                  <Th className="text-right">Nettó bér</Th>
                  <Th className="text-right">Jutalék (br/nt)</Th>
                  <Th className="text-right">Bónusz (br/nt)</Th>
                  <Th className="text-right">Óra</Th>
                  <Th>Állapot</Th>
                  <Th className="pr-4 text-right">Műveletek</Th>
                </tr>
              </thead>
              <tbody>
                {salaries.map((s) => (
                  <tr
                    key={s._id}
                    data-testid={`salary-row-${s._id}`}
                    className="border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]"
                  >
                    <Td>
                      <span className="font-medium">{formatPayMonth(s.pay_month)}</span>
                    </Td>
                    <Td className="text-right font-mono text-[12.5px]">
                      {formatHuf(s.gross_salary)}
                    </Td>
                    <Td className="text-right font-mono text-[12.5px]">
                      {formatHuf(s.net_salary)}
                    </Td>
                    <Td className="text-right font-mono text-[12px] text-muted-foreground">
                      {formatHuf(s.gross_commission)}
                      {" / "}
                      {formatHuf(s.net_commission)}
                    </Td>
                    <Td className="text-right font-mono text-[12px] text-muted-foreground">
                      {formatHuf(s.gross_bonus)}
                      {" / "}
                      {formatHuf(s.net_bonus)}
                    </Td>
                    <Td className="text-right font-mono text-[12.5px] text-muted-foreground">
                      {formatHours(s.hours_worked)}
                    </Td>
                    <Td>
                      <SalaryStatusBadge value={s.status} />
                    </Td>
                    <Td className="pr-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        {s.status === "waiting_accountant" && (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`account-salary-${s._id}`}
                            onClick={() => onAccount(s)}
                            className="h-8 px-2.5 text-[12px]"
                          >
                            <Wallet className="h-3.5 w-3.5" strokeWidth={1.75} />
                            Könyvelés
                          </Button>
                        )}
                        {s.status === "waiting_payment" && (
                          <Button
                            size="sm"
                            data-testid={`pay-salary-${s._id}`}
                            onClick={() => onPay(s)}
                            disabled={payingId === s._id}
                            className="h-8 px-2.5 text-[12px]"
                          >
                            {payingId === s._id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Wallet className="h-3.5 w-3.5" strokeWidth={1.75} />
                            )}
                            Kifizetés
                          </Button>
                        )}
                        {s.payslip_url && (
                          <a
                            data-testid={`payslip-link-${s._id}`}
                            href="#"
                            onClick={(ev) => {
                              ev.preventDefault();
                              ev.stopPropagation();
                              openSalaryPayslip(s);
                            }}
                            className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                            aria-label="Bérjegyzék megnyitása"
                            title="Bérjegyzék"
                          >
                            <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
                          </a>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              data-testid={`salary-actions-${s._id}`}
                              aria-label="Bér műveletek"
                              className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuLabel>Műveletek</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {s.status === "waiting_accountant" && (
                              <DropdownMenuItem
                                data-testid={`menu-account-salary-${s._id}`}
                                onClick={() => onAccount(s)}
                              >
                                <Wallet className="h-4 w-4" />
                                Könyvelés
                              </DropdownMenuItem>
                            )}
                            {s.status === "waiting_payment" && (
                              <DropdownMenuItem
                                data-testid={`menu-pay-salary-${s._id}`}
                                onClick={() => onPay(s)}
                              >
                                <Wallet className="h-4 w-4" />
                                Kifizetés
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              data-testid={`delete-salary-${s._id}`}
                              onClick={() => onDelete(s)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                              Törlés
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SalaryStatCard({ icon, label, value, mono }) {
  return (
    <div className="rounded-xl border border-border bg-[hsl(var(--sipx-elevated))] p-4">
      <p className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        <span className="text-muted-foreground">{icon}</span>
        {label}
      </p>
      <p
        className={cn(
          "mt-1.5 text-xl font-semibold tracking-tight",
          mono && "font-mono"
        )}
      >
        {value}
      </p>
    </div>
  );
}
